import { useCallback, useEffect, useRef, useState } from 'react';

const FOCUS_DELAY_MS = 50;

export interface UseToolbarSearchOptions {
  searchValue: string;
  onSearchChange?: (value: string) => void;
}

export interface UseToolbarSearchReturn {
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchOpen: boolean;
  localSearchValue: string;
  handleSearchClick: () => void;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchBlur: () => void;
  handleSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const useToolbarSearch = ({
  searchValue,
  onSearchChange,
}: UseToolbarSearchOptions): UseToolbarSearchReturn => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchOpen, setSearchOpen] = useState(Boolean(searchValue));
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);

  useEffect(() => {
    setLocalSearchValue(searchValue);
    if (searchValue) setSearchOpen(true);
  }, [searchValue]);

  const handleSearchClick = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), FOCUS_DELAY_MS);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchValue(value);
      onSearchChange?.(value);
    },
    [onSearchChange],
  );

  const handleSearchBlur = useCallback(() => {
    if (!localSearchValue.trim()) setSearchOpen(false);
  }, [localSearchValue]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Escape') return;
      setLocalSearchValue('');
      setSearchOpen(false);
      onSearchChange?.('');
    },
    [onSearchChange],
  );

  return {
    searchInputRef,
    searchOpen,
    localSearchValue,
    handleSearchClick,
    handleSearchChange,
    handleSearchBlur,
    handleSearchKeyDown,
  };
};

export default useToolbarSearch;
